import React, { useEffect, useMemo, useState } from "react";
import { Card, CardBody, Col, Container, Modal, Row } from "reactstrap";
import ReactApexChart from "react-apexcharts";
import Breadcrumb from "components/Common/Breadcrumb";
import Select from "react-select";
import { fetchNodeData } from "utils/common-fetchData";
import Loader from "pages/Separate/Loader";

// ─── Financial Year Generator ────────────────────────────────────────────────
const generateFYOptions = () => {
  const now = new Date();
  const currentMonth = now.getMonth() + 1;
  const currentYear = now.getFullYear();
  const currentFYStart = currentMonth >= 4 ? currentYear : currentYear - 1;

  const options = [];
  for (let y = 2018; y <= currentFYStart; y++) {
    const label = `${y}-${String(y + 1).slice(-2)}`;
    options.push({ label, value: label });
  }
  return options.reverse();
};

const FY_OPTIONS = generateFYOptions();

const PURPLE = "#981f77";
const MOD_PURPLE = "#752a97";

// ─── Helpers ─────────────────────────────────────────────────────────────────
const isMonthIncomplete = (monthKey) => {
  const [yr, mn] = monthKey.split("-").map(Number);
  const now = new Date();
  return (
    yr > now.getFullYear() ||
    (yr === now.getFullYear() && mn > now.getMonth() + 1)
  );
};

const fmt = (n) =>
  "₹ " +
  Math.abs(n).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const selectStyles = {
  control: (s) => ({
    ...s,
    borderColor: "#ced4da",
    boxShadow: "none",
    minWidth: 140,
  }),
  menuList: (s) => ({ ...s, background: "white" }),
  menu: (s) => ({ ...s, zIndex: 50 }),
};

// ─── Component ────────────────────────────────────────────────────────────────
const FinanceReport = () => {
  document.title = "Finance Report | Zithas Crm";

  const [selectedFY, setSelectedFY] = useState(FY_OPTIONS[0]);
  const [activeIdx, setActiveIdx] = useState(null);
  const [loading, setLoading] = useState(false);
  const [months, setMonths] = useState([]);

  useEffect(() => {
    fetchFinancialReport(selectedFY.value);
  }, [selectedFY]);

  async function fetchFinancialReport(fyValue) {
    const startYear = parseInt(fyValue.split("-")[0], 10);
    const endYear = startYear + 1;

    const res = await fetchNodeData(
      setLoading,
      `financial_report?start_month=4&start_year=${startYear}&end_month=3&end_year=${endYear}`,
    );

    if (res?.success) {
      setMonths(
        (res.months || []).map((m) => ({
          month: m.month_label,
          month_key: m.month_key,
          income: {
            total: m.income.total,
            items: m.income.transactions.map((t) => ({
              customer_name: t.customer_name,
              invoice_number: t.invoice_number,
              amount: t.converted_amount,
              date: t.receive_date,
              payment_mode: t.payment_mode,
              source: t.source,
            })),
          },
          expense: {
            total: m.expense.total,
            items: m.expense.entries.map((e) => ({
              desc: e.title || "—",
              amount: e.amount,
              date: e.date,
              type: e.type,
              category: e.category,
            })),
          },
        })),
      );
    }
  }

  const ledger = useMemo(() => {
    let bf = 0;
    return months.map((m) => {
      const net = m.income.total - m.expense.total;
      const closing = bf + net;
      const row = { ...m, bf, net, closing };
      bf = closing;
      return row;
    });
  }, [months]);

  const totals = useMemo(
    () => ({
      income: ledger.reduce((s, r) => s + r.income.total, 0),
      expense: ledger.reduce((s, r) => s + r.expense.total, 0),
      net: ledger.reduce((s, r) => s + r.net, 0),
    }),
    [ledger],
  );

  // ── Chart ──
  const chartSeries = [{ name: "Monthly Net", data: ledger.map((r) => r.net) }];
  const chartOptions = {
    chart: {
      type: "area",
      toolbar: { show: false },
      background: "transparent",
    },
    colors: [PURPLE],
    stroke: { width: 2.5, curve: "smooth" },
    fill: {
      type: "gradient",
      gradient: {
        shade: "light",
        type: "vertical",
        shadeIntensity: 0.4,
        opacityFrom: 0.55,
        opacityTo: 0.03,
      },
    },
    dataLabels: {
      enabled: true,
      formatter: (v) =>
        (v < 0 ? "-₹" : "₹") + Math.abs(v / 1000).toFixed(0) + "K",
      style: { fontSize: "9px", colors: ["#333"] },
      background: { enabled: false },
      offsetY: -7,
    },
    xaxis: {
      categories: ledger.map((r) => r.month.split(" ")[0]),
      labels: { style: { fontSize: "11px" } },
    },
    yaxis: {
      labels: {
        formatter: (v) =>
          (v < 0 ? "-₹" : "₹") + Math.abs(v / 1000).toFixed(0) + "K",
        style: { fontSize: "11px" },
      },
    },
    annotations: {
      yaxis: [
        {
          y: 0,
          borderColor: "#bbb",
          strokeDashArray: 4,
          label: {
            text: "Break-even",
            style: { fontSize: "10px", color: "#888" },
          },
        },
      ],
    },
    legend: { show: false },
    tooltip: {
      shared: true,
      intersect: false,
      y: { formatter: (v) => (v < 0 ? "-" : "") + fmt(v) },
    },
    grid: { borderColor: "#f1f1f1" },
    markers: { size: 4, colors: [PURPLE], strokeWidth: 0 },
  };

  const active = activeIdx !== null ? ledger[activeIdx] : null;

  return (
    <div className="page-content">
      <style>{`
        .thin-scroll::-webkit-scrollbar { width: 4px; }
        .thin-scroll::-webkit-scrollbar-track { background: transparent; }
        .thin-scroll::-webkit-scrollbar-thumb { background: #ccc; border-radius: 4px; }
        .thin-scroll::-webkit-scrollbar-thumb:hover { background: #aaa; }
      `}</style>
      {loading && <Loader />}
      <Container fluid>
        <Breadcrumb
          title="Reports"
          breadcrumbItem={`Finance Overview — FY ${selectedFY.label}`}
        />

        <Card>
          <CardBody>
            {/* Header — summary boxes + FY selector */}
            <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-3">
              {ledger.length > 0 ? (
                <div className="d-flex gap-3 flex-wrap">
                  {[
                    {
                      label: "Total Income",
                      value: totals.income,
                      color: "text-success",
                    },
                    {
                      label: "Total Expense",
                      value: totals.expense,
                      color: "text-danger",
                    },
                    {
                      label: "Net Profit / Loss",
                      value: totals.net,
                      color: totals.net >= 0 ? "text-success" : "text-danger",
                    },
                  ].map((box, i) => (
                    <div
                      key={i}
                      className="budget_status pt-2 px-3 pb-2 rounded"
                      style={{
                        backgroundColor: "#5c038517",
                        minWidth: "160px",
                        cursor: "default",
                      }}
                    >
                      <div
                        className="text-muted mb-1"
                        style={{ fontSize: "12px" }}
                      >
                        {box.label}
                      </div>
                      <div className={`fw-bold fs-6 mb-0 ${box.color}`}>
                        {box.value < 0 && "-"}
                        {fmt(box.value)}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <h5 className="mb-0 fw-bold" style={{ color: PURPLE }}>
                  Finance Overview
                </h5>
              )}
              <div className="d-flex align-items-center gap-2">
                <label
                  className="mb-0 text-muted"
                  style={{ fontSize: "13px", whiteSpace: "nowrap" }}
                >
                  Financial Year
                </label>
                <Select
                  options={FY_OPTIONS}
                  value={selectedFY}
                  styles={selectStyles}
                  classNamePrefix="select"
                  onChange={(opt) => {
                    setSelectedFY(opt);
                    setActiveIdx(null);
                  }}
                />
              </div>
            </div>

            {/* Table LEFT + Chart RIGHT */}
            <Row className="g-3">
              {/* Table */}
              <Col md={5}>
                <div className="table-responsive">
                  <table
                    className="table table-sm table-bordered mb-0"
                    style={{ fontSize: "12.5px" }}
                  >
                    <thead
                      className="text-white"
                      style={{ backgroundColor: MOD_PURPLE }}
                    >
                      <tr>
                        <th>Month</th>
                        <th className="text-end">Received (₹)</th>
                        <th className="text-end">Paid (₹)</th>
                        <th className="text-end">Closing (₹)</th>
                        <th className="text-center">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {ledger.length > 0 ? (
                        ledger.map((row, i) => {
                          const incomplete = isMonthIncomplete(row.month_key);
                          return (
                            <tr
                              key={i}
                              className="row-hover"
                              style={{ cursor: "pointer" }}
                              onClick={() => setActiveIdx(i)}
                            >
                              <td className="fw-semibold">{row.month}</td>
                              <td className="text-end text-success">
                                {fmt(row.income.total)}
                              </td>
                              <td className="text-end text-danger">
                                {fmt(row.expense.total)}
                              </td>
                              <td
                                className="text-end fw-semibold"
                                style={{
                                  color: incomplete
                                    ? "#888"
                                    : row.closing >= 0
                                    ? "#1a7a50"
                                    : "#c0392b",
                                }}
                              >
                                {incomplete
                                  ? "—"
                                  : (row.closing < 0 ? "-" : "") +
                                    fmt(row.closing)}
                              </td>
                              <td className="text-center">
                                {incomplete ? (
                                  <i
                                    className="fas fa-minus text-muted"
                                    style={{ fontSize: "13px" }}
                                  ></i>
                                ) : row.net >= 0 ? (
                                  <i
                                    className="fas fa-arrow-up text-success"
                                    style={{ fontSize: "14px" }}
                                  ></i>
                                ) : (
                                  <i
                                    className="fas fa-arrow-down text-danger"
                                    style={{ fontSize: "14px" }}
                                  ></i>
                                )}
                              </td>
                            </tr>
                          );
                        })
                      ) : (
                        <tr>
                          <td
                            colSpan={5}
                            className="text-center text-muted py-4"
                          >
                            No data for {selectedFY.label}
                          </td>
                        </tr>
                      )}
                    </tbody>
                    {ledger.length > 0 && (
                      <tfoot>
                        <tr
                          className="fw-bold"
                          style={{ backgroundColor: "#f3e5f5" }}
                        >
                          <td>TOTAL</td>
                          <td className="text-end text-success">
                            {fmt(totals.income)}
                          </td>
                          <td className="text-end text-danger">
                            {fmt(totals.expense)}
                          </td>
                          <td
                            className="text-end"
                            style={{
                              color: totals.net >= 0 ? "#1a7a50" : "#c0392b",
                            }}
                          >
                            {totals.net < 0 && "-"}
                            {fmt(totals.net)}
                          </td>
                          <td className="text-center">
                            {totals.net >= 0 ? (
                              <i
                                className="fas fa-arrow-up text-success"
                                style={{ fontSize: "14px" }}
                              ></i>
                            ) : (
                              <i
                                className="fas fa-arrow-down text-danger"
                                style={{ fontSize: "14px" }}
                              ></i>
                            )}
                          </td>
                        </tr>
                      </tfoot>
                    )}
                  </table>
                </div>
              </Col>

              {/* Chart */}
              <Col md={7}>
                <p
                  className="text-center fw-semibold mb-1"
                  style={{ fontSize: "13px", color: "#495057" }}
                >
                  Monthly Cash Flow Net Trend
                </p>
                {ledger.length > 0 ? (
                  <ReactApexChart
                    options={chartOptions}
                    series={chartSeries}
                    type="area"
                    height={340}
                  />
                ) : (
                  <div
                    className="d-flex align-items-center justify-content-center"
                    style={{ height: 340 }}
                  >
                    <span className="text-muted">
                      No data available for {selectedFY.label}
                    </span>
                  </div>
                )}
              </Col>
            </Row>
          </CardBody>
        </Card>

        {/* ── Month Detail Modal ── */}
        <Modal
          isOpen={activeIdx !== null}
          size="xl"
          centered
          toggle={() => setActiveIdx(null)}
        >
          {active && (
            <>
              <div
                className="modal-header"
                style={{
                  backgroundColor: "#ede0f5",
                  borderBottom: "1px solid #c8a0de",
                }}
              >
                <h5
                  className="modal-title fw-bold"
                  style={{ color: MOD_PURPLE }}
                >
                  {active.month} — Detail View
                </h5>
                <div className="d-flex gap-2 align-items-center">
                  <button
                    className="btn btn-sm border-radius"
                    style={{
                      border: `1px solid ${MOD_PURPLE}`,
                      color: MOD_PURPLE,
                    }}
                    disabled={activeIdx === 0}
                    onClick={() => setActiveIdx((i) => i - 1)}
                  >
                    <i className="fas fa-chevron-left me-1"></i>
                    {activeIdx > 0 ? ledger[activeIdx - 1].month : "Prev"}
                  </button>
                  <button
                    className="btn btn-sm border-radius"
                    style={{
                      border: `1px solid ${MOD_PURPLE}`,
                      color: MOD_PURPLE,
                    }}
                    disabled={activeIdx === ledger.length - 1}
                    onClick={() => setActiveIdx((i) => i + 1)}
                  >
                    {activeIdx < ledger.length - 1
                      ? ledger[activeIdx + 1].month
                      : "Next"}
                    <i className="fas fa-chevron-right ms-1"></i>
                  </button>
                  <button
                    type="button"
                    className="btn-close ms-2"
                    onClick={() => setActiveIdx(null)}
                  />
                </div>
              </div>
              <div className="modal-body">
                {/* B/F — Net — Closing cards */}
                <Row className="g-3 mb-4">
                  {[
                    {
                      label: "This Month Net",
                      value: active.net,
                      bg: active.net >= 0 ? "#eaf4ee" : "#fdecea",
                      color: active.net >= 0 ? "#2e6b45" : "#a93226",
                    },
                    {
                      label: "Closing Balance",
                      value: isMonthIncomplete(active.month_key)
                        ? null
                        : active.closing,
                      bg: isMonthIncomplete(active.month_key)
                        ? "#f5f5f5"
                        : active.closing >= 0
                        ? "#eaf4ee"
                        : "#fdecea",
                      color: isMonthIncomplete(active.month_key)
                        ? "#888"
                        : active.closing >= 0
                        ? "#1e4d33"
                        : "#922b21",
                    },
                  ].map((card, i) => (
                    <Col md={6} key={i}>
                      <div
                        className="rounded p-3 h-100"
                        style={{
                          backgroundColor: card.bg,
                          border: `1px solid ${card.color}40`,
                        }}
                      >
                        <div
                          className="small mb-1"
                          style={{ color: card.color, opacity: 0.7 }}
                        >
                          {card.label}
                        </div>
                        <div
                          className="fs-5 fw-bold"
                          style={{ color: card.color }}
                        >
                          {card.value === null
                            ? "—"
                            : (card.value < 0 ? "-" : "") + fmt(card.value)}
                        </div>
                      </div>
                    </Col>
                  ))}
                </Row>

                {/* Income + Expense tables */}
                <Row className="g-3">
                  {/* Income */}
                  <Col md={6}>
                    <Card className="h-100 mb-0">
                      <CardBody className="p-0">
                        <div
                          className="d-flex justify-content-between align-items-center px-3 py-2 rounded-top"
                          style={{
                            backgroundColor: "#eaf4ee",
                            borderBottom: "1px solid #c3dfc9",
                          }}
                        >
                          <span
                            className="fw-semibold"
                            style={{ fontSize: "13px", color: "#2e6b45" }}
                          >
                            <i className="fas fa-arrow-circle-down me-2"></i>
                            Income
                          </span>
                          <span
                            className="fw-semibold"
                            style={{ color: "#2e6b45" }}
                          >
                            {fmt(active.income.total)}
                          </span>
                        </div>
                        <div
                          className="table-responsive thin-scroll"
                          style={{
                            maxHeight: "360px",
                            overflowY: "auto",
                            scrollbarWidth: "thin",
                            scrollbarColor: "#ccc transparent",
                          }}
                        >
                          <table
                            className="table table-sm table-bordered mb-0"
                            style={{ fontSize: "12.5px" }}
                          >
                            <thead
                              className="table-light"
                              style={{
                                position: "sticky",
                                top: 0,
                                zIndex: 2,
                                backgroundColor: "#f8f9fa",
                              }}
                            >
                              <tr>
                                <th>#</th>
                                <th>Invoice #</th>
                                <th>Customer</th>
                                <th className="text-end w-25">Amount</th>

                                <th className="text-center">Date</th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr style={{ backgroundColor: "#ede0f5" }}>
                                <td className="text-muted">—</td>
                                <td
                                  colSpan={2}
                                  className="fw-semibold"
                                  style={{ color: MOD_PURPLE }}
                                >
                                  Balance Brought Forward
                                </td>
                                <td
                                  className="text-end fw-semibold"
                                  style={{ color: MOD_PURPLE }}
                                >
                                  {activeIdx === 0 ? "—" : fmt(active.bf)}
                                </td>
                                <td className="text-center text-muted">—</td>
                              </tr>
                              {active.income.items.length > 0 ? (
                                active.income.items.map((item, i) => (
                                  <tr key={i} className="row-hover">
                                    <td className="text-muted">{i + 1}</td>
                                    <td style={{ fontSize: "11px" }}>
                                      {item.invoice_number}
                                    </td>
                                    <td>
                                      {item.customer_name} (
                                      {item.payment_mode || "—"})
                                    </td>
                                    <td className="text-end text-success fw-semibold">
                                      {fmt(item.amount)}
                                    </td>

                                    <td className="text-center text-muted">
                                      {item.date || "—"}
                                    </td>
                                  </tr>
                                ))
                              ) : (
                                <tr>
                                  <td
                                    colSpan={6}
                                    className="text-center text-muted py-3"
                                  >
                                    No income items
                                  </td>
                                </tr>
                              )}
                            </tbody>
                            <tfoot
                              style={{
                                position: "sticky",
                                bottom: 0,
                                zIndex: 2,
                              }}
                            >
                              <tr
                                className="fw-bold"
                                style={{ backgroundColor: "#eafaf1" }}
                              >
                                <td colSpan={3}>Total</td>
                                <td className="text-end text-success">
                                  {fmt(active.income.total)}
                                </td>
                                <td colSpan={2}></td>
                              </tr>
                            </tfoot>
                          </table>
                        </div>
                      </CardBody>
                    </Card>
                  </Col>

                  {/* Expense */}
                  <Col md={6}>
                    <Card className="h-100 mb-0">
                      <CardBody className="p-0">
                        <div
                          className="d-flex justify-content-between align-items-center px-3 py-2 rounded-top"
                          style={{
                            backgroundColor: "#fdecea",
                            borderBottom: "1px solid #f0c4c0",
                          }}
                        >
                          <span
                            className="fw-semibold"
                            style={{ fontSize: "13px", color: "#a93226" }}
                          >
                            <i className="fas fa-arrow-circle-up me-2"></i>
                            Expense
                          </span>
                          <span
                            className="fw-semibold"
                            style={{ color: "#a93226" }}
                          >
                            {fmt(active.expense.total)}
                          </span>
                        </div>
                        <div
                          className="table-responsive thin-scroll"
                          style={{
                            maxHeight: "360px",
                            overflowY: "auto",
                            scrollbarWidth: "thin",
                            scrollbarColor: "#ccc transparent",
                          }}
                        >
                          <table
                            className="table table-sm table-bordered mb-0"
                            style={{ fontSize: "12.5px" }}
                          >
                            <thead
                              className="table-light"
                              style={{
                                position: "sticky",
                                top: 0,
                                zIndex: 2,
                                backgroundColor: "#f8f9fa",
                              }}
                            >
                              <tr>
                                <th>#</th>
                                <th>Title</th>
                                <th className="text-center">Type</th>
                                <th className="text-end">Amount</th>
                                <th className="text-center">Date</th>
                              </tr>
                            </thead>
                            <tbody>
                              {active.expense.items.length > 0 ? (
                                active.expense.items.map((item, i) => (
                                  <tr key={i} className="row-hover">
                                    <td className="text-muted">{i + 1}</td>
                                    <td>{item.desc}</td>
                                    <td className="text-center">
                                      {item.type === "vendor" ? (
                                        <span
                                          className="badge"
                                          style={{
                                            backgroundColor: "#752a9720",
                                            color: MOD_PURPLE,
                                            fontSize: "10px",
                                          }}
                                        >
                                          Vendor
                                        </span>
                                      ) : item.type === "budget" ? (
                                        <span
                                          className="badge"
                                          style={{
                                            backgroundColor: "#1a7a5020",
                                            color: "#2e6b45",
                                            fontSize: "10px",
                                          }}
                                        >
                                          Budget
                                        </span>
                                      ) : (
                                        <span
                                          className="badge"
                                          style={{
                                            backgroundColor: "#a9322620",
                                            color: "#a93226",
                                            fontSize: "10px",
                                          }}
                                        >
                                          Actual
                                        </span>
                                      )}
                                    </td>
                                    <td className="text-end text-danger fw-semibold">
                                      {fmt(item.amount)}
                                    </td>
                                    <td className="text-center text-muted">
                                      {item.date || "—"}
                                    </td>
                                  </tr>
                                ))
                              ) : (
                                <tr>
                                  <td
                                    colSpan={5}
                                    className="text-center text-muted py-3"
                                  >
                                    No expense items
                                  </td>
                                </tr>
                              )}
                            </tbody>
                            <tfoot
                              style={{
                                position: "sticky",
                                bottom: 0,
                                zIndex: 2,
                              }}
                            >
                              <tr
                                className="fw-bold"
                                style={{ backgroundColor: "#fde8e8" }}
                              >
                                <td colSpan={3}>Total</td>
                                <td className="text-end text-danger">
                                  {fmt(active.expense.total)}
                                </td>
                                <td></td>
                              </tr>
                            </tfoot>
                          </table>
                        </div>
                      </CardBody>
                    </Card>
                  </Col>
                </Row>
              </div>
            </>
          )}
        </Modal>
      </Container>
    </div>
  );
};

export default FinanceReport;

